import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, X, Save, Calendar as CalIcon, MapPin, Ruler, Handshake, Truck, Hammer, CheckSquare, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
    zamereni:   { label: "Zaměření", icon: Ruler, color: "bg-blue-500", chip: "bg-blue-100 text-blue-700 border-blue-300" },
    schuzka:    { label: "Schůzka", icon: Handshake, color: "bg-purple-500", chip: "bg-purple-100 text-purple-700 border-purple-300" },
    dodavka:    { label: "Dodávka materiálu", icon: Truck, color: "bg-amber-500", chip: "bg-amber-100 text-amber-700 border-amber-300" },
    prace:      { label: "Pracovní den", icon: Hammer, color: "bg-orange-500", chip: "bg-orange-100 text-orange-700 border-orange-300" },
    kontrola:   { label: "Kontrola", icon: CheckSquare, color: "bg-teal-500", chip: "bg-teal-100 text-teal-700 border-teal-300" },
    kolaudace:  { label: "Kolaudace / předání", icon: ClipboardCheck, color: "bg-green-500", chip: "bg-green-100 text-green-700 border-green-300" },
    jine:       { label: "Jiné", icon: CalIcon, color: "bg-slate-500", chip: "bg-slate-100 text-slate-700 border-slate-300" },
};

const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const WD = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

const EMPTY = {
    title: "", type: "schuzka", description: "", job_id: "",
    start_datetime: "", end_datetime: "", all_day: false,
    assignee_user_ids: [], location: "",
};

export default function Planner() {
    const today = new Date();
    const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
    const [events, setEvents] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [members, setMembers] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        const first = new Date(cursor.y, cursor.m, 1).toISOString().slice(0, 10);
        const lastD = new Date(cursor.y, cursor.m + 1, 0);
        const last = lastD.toISOString().slice(0, 10);
        const { data } = await api.get(`/events?from_date=${first}&to_date=${last}`);
        setEvents(data);
    };
    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data));
        api.get("/team/members").then((r) => setMembers(r.data));
    }, []);
    useEffect(() => { load(); }, [cursor.y, cursor.m]); // eslint-disable-line react-hooks/exhaustive-deps

    const days = useMemo(() => {
        const first = new Date(cursor.y, cursor.m, 1);
        const startDow = (first.getDay() + 6) % 7;
        const last = new Date(cursor.y, cursor.m + 1, 0);
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(cursor.y, cursor.m, d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [cursor]);

    const byDay = useMemo(() => {
        const m = {};
        for (const e of events) {
            const key = (e.start_datetime || "").slice(0, 10);
            (m[key] = m[key] || []).push(e);
        }
        return m;
    }, [events]);

    const openNew = (d) => {
        const dstr = d ? d.toISOString().slice(0, 10) : today.toISOString().slice(0, 10);
        setEditing({ ...EMPTY, start_datetime: `${dstr}T09:00`, end_datetime: `${dstr}T10:00` });
    };

    const save = async () => {
        if (!editing.title.trim()) { toast.error("Vyplňte název akce"); return; }
        if (!editing.start_datetime) { toast.error("Vyplňte datum a čas"); return; }
        const payload = {
            title: editing.title, type: editing.type, description: editing.description || "",
            job_id: editing.job_id || null, start_datetime: editing.start_datetime,
            end_datetime: editing.end_datetime || null, all_day: !!editing.all_day,
            assignee_user_ids: editing.assignee_user_ids || [], location: editing.location || "",
        };
        try {
            if (editing.id) await api.put(`/events/${editing.id}`, payload);
            else await api.post("/events", payload);
            toast.success("Uloženo");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const del = async () => {
        if (!editing?.id) return;
        if (!window.confirm("Smazat akci?")) return;
        await api.delete(`/events/${editing.id}`);
        setEditing(null);
        load();
    };

    const prev = () => setCursor((c) => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
    const next = () => setCursor((c) => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });
    const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });
    const dk = (d) => d ? d.toISOString().slice(0, 10) : "";
    const isToday = (d) => d && dk(d) === today.toISOString().slice(0, 10);

    return (
        <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto animate-fade-in" data-testid="planner-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow mb-2">Plánovač</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Kalendář akcí</h1>
                    <p className="text-sm text-slate-600 mt-2">Zaměření, schůzky, dodávky, kolaudace — celý provoz firmy na jednom místě.</p>
                </div>
                <button onClick={() => openNew(today)} data-testid="new-event-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nová akce
                </button>
            </div>

            <div className="bg-white border border-slate-200">
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                    <div className="font-display text-xl font-bold text-slate-900">{MONTHS[cursor.m]} {cursor.y}</div>
                    <div className="flex items-center gap-1">
                        <button onClick={prev} className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm" data-testid="planner-prev">‹</button>
                        <button onClick={goToday} className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Dnes</button>
                        <button onClick={next} className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm" data-testid="planner-next">›</button>
                    </div>
                </div>
                <div className="grid grid-cols-7 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                    {WD.map((w) => <div key={w} className="px-3 py-2 text-center font-semibold">{w}</div>)}
                </div>
                <div className="grid grid-cols-7">
                    {days.map((d, i) => {
                        const items = d ? (byDay[dk(d)] || []) : [];
                        return (
                            <div key={i} onClick={() => d && openNew(d)}
                                 className={`min-h-[120px] border-r border-b border-slate-100 p-2 cursor-pointer hover:bg-slate-50 ${!d ? "bg-slate-50/60 cursor-default" : ""} ${isToday(d) ? "bg-orange-50/40" : ""}`}>
                                {d && (
                                    <>
                                        <div className={`text-xs font-semibold mb-1.5 tabular-data ${isToday(d) ? "text-orange-700" : "text-slate-700"}`}>{d.getDate()}</div>
                                        <div className="space-y-1">
                                            {items.slice(0, 4).map((e) => {
                                                const meta = TYPE_META[e.type] || TYPE_META.jine;
                                                return (
                                                    <button key={e.id} onClick={(evt) => { evt.stopPropagation(); setEditing(e); }}
                                                            data-testid={`event-${e.id}`}
                                                            className={`w-full text-left ${meta.color} text-white text-[10px] px-1.5 py-0.5 rounded-sm hover:opacity-80 truncate flex items-center gap-1`}>
                                                        <meta.icon className="w-3 h-3 flex-shrink-0" />
                                                        <span className="truncate">
                                                            {(e.start_datetime || "").slice(11, 16)} {e.title}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                            {items.length > 4 && <div className="text-[10px] text-slate-500">+ {items.length - 4} dalších</div>}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white border-2 border-slate-900 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                            <h2 className="font-display text-lg font-bold">{editing.id ? "Upravit akci" : "Nová akce"}</h2>
                            <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <Field label="Název *" v={editing.title} on={(e) => setEditing({ ...editing, title: e.target.value })} testid="event-title" />
                            <div>
                                <label className="label-eyebrow block mb-1.5">Typ</label>
                                <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="event-type"
                                        className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm">
                                    {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Začátek *" type="datetime-local" v={editing.start_datetime} on={(e) => setEditing({ ...editing, start_datetime: e.target.value })} testid="event-start" />
                                <Field label="Konec" type="datetime-local" v={editing.end_datetime} on={(e) => setEditing({ ...editing, end_datetime: e.target.value })} />
                            </div>
                            <Field label="Místo" v={editing.location} on={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="Adresa stavby / kanceláře" />
                            <div>
                                <label className="label-eyebrow block mb-1.5">Zakázka (volitelné)</label>
                                <select value={editing.job_id || ""} onChange={(e) => setEditing({ ...editing, job_id: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm">
                                    <option value="">— žádná —</option>
                                    {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1.5">Přiřazeno komu</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {members.map((m) => {
                                        const on = (editing.assignee_user_ids || []).includes(m.id);
                                        return (
                                            <button key={m.id} type="button"
                                                    onClick={() => {
                                                        const cur = editing.assignee_user_ids || [];
                                                        setEditing({ ...editing, assignee_user_ids: on ? cur.filter((x) => x !== m.id) : [...cur, m.id] });
                                                    }}
                                                    className={`text-xs px-2 py-1 border ${on ? "bg-orange-600 text-white border-orange-600" : "border-slate-300 hover:bg-slate-100"}`}>
                                                {m.name || m.email}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1.5">Poznámka</label>
                                <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                          className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm resize-none" />
                            </div>
                        </div>
                        <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-between">
                            <div>{editing.id && <button onClick={del} className="px-4 py-2 text-xs uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50 rounded-sm">Smazat</button>}</div>
                            <div className="flex gap-2">
                                <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zrušit</button>
                                <button onClick={save} data-testid="event-save" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1">
                                    <Save className="w-3.5 h-3.5" /> Uložit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, v, on, type = "text", testid, placeholder }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <input type={type} value={v || ""} onChange={on} placeholder={placeholder} data-testid={testid}
                   className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
        </div>
    );
}
